// EventBus — standalone pub/sub, no Phaser dependency

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Listener = (...args: any[]) => void

class GameEventBus {
  private static instance: GameEventBus
  private listeners: Map<string, Set<Listener>> = new Map()

  private constructor() {}

  public static getInstance(): GameEventBus {
    if (!GameEventBus.instance) {
      GameEventBus.instance = new GameEventBus()
    }
    return GameEventBus.instance
  }

  on(event: string, listener: Listener): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(listener)
    return this
  }

  off(event: string, listener: Listener): this {
    this.listeners.get(event)?.delete(listener)
    return this
  }

  once(event: string, listener: Listener): this {
    const wrapper: Listener = (...args) => {
      listener(...args)
      this.off(event, wrapper)
    }
    return this.on(event, wrapper)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emit(event: string, ...args: any[]): this {
    this.listeners.get(event)?.forEach(l => l(...args))
    return this
  }

  removeAllListeners(event?: string): this {
    if (event) {
      this.listeners.delete(event)
    } else {
      this.listeners.clear()
    }
    return this
  }
}

export const EventBus = GameEventBus.getInstance()

// Event types
export const GameEvents = {
  // Game flow
  GAME_STARTED: 'game:started',
  GAME_PAUSED: 'game:paused',
  GAME_RESUMED: 'game:resumed',
  GAME_OVER: 'game:over',
  VICTORY: 'game:victory',

  // Time
  DAY_CHANGED: 'time:day_changed',
  WEEK_CHANGED: 'time:week_changed',
  MONTH_CHANGED: 'time:month_changed',

  // Economy
  MONEY_CHANGED: 'economy:money_changed',
  TRANSACTION: 'economy:transaction',

  // Courts / building
  COURT_PLACED: 'court:placed',
  COURT_SELECTED: 'court:selected',
  BUILD_MENU_OPENED: 'build:menu_opened',
  BUILD_MODE_ON: 'build:mode_on',
  BUILD_MODE_OFF: 'build:mode_off',
  // A queued construction (court / building / upgrade) finished building
  CONSTRUCTION_COMPLETED: 'construction:completed',

  // Generic UI toast request (message + type)
  NOTIFY: 'ui:notify',

  // Weather
  WEATHER_CHANGED: 'weather:changed',

  // Special events
  EVENT_TRIGGERED: 'event:triggered',
  EVENT_RESOLVED: 'event:resolved',

  // Achievements
  ACHIEVEMENT_UNLOCKED: 'achievement:unlocked',

  // Competition milestones (celebration layer listens to these)
  LEAGUE_SEASON_ENDED: 'league:season_ended',
  TOURNAMENT_WON: 'tournament:won',
  // A Circuito FIP event the club entered was resolved
  CIRCUIT_EVENT_RESOLVED: 'circuit:event_resolved',

  // Roster health
  PLAYER_INJURED: 'roster:player_injured',
  PLAYER_RECOVERED: 'roster:player_recovered',

  // Career / board
  BOARD_OBJECTIVE_SET: 'board:objective_set',
  BOARD_OBJECTIVE_MET: 'board:objective_met',
  BOARD_OBJECTIVE_FAILED: 'board:objective_failed',
  BOARD_CONFIDENCE_CHANGED: 'board:confidence_changed',
  MANAGER_FIRED: 'board:manager_fired',
  CLUB_OFFER_RECEIVED: 'career:offer_received',
  CLUB_CHANGED: 'career:club_changed',
} as const

export interface LeagueSeasonEndedPayload {
  season: number
  division: number
  divisionName: string
  position: number
  promoted: boolean
  relegated: boolean
  title: boolean
}

export interface TournamentWonPayload {
  tournamentName: string
  prize: number
}

export interface CircuitEventResolvedPayload {
  name: string
  tier: string
  champion: boolean
  points: number
  prize: number
}

export interface PlayerInjuredPayload {
  playerId: string
  playerName: string
  injuryName: string
  severity: 'leve' | 'moderada' | 'grave'
  totalDays: number
}

export interface PlayerRecoveredPayload {
  playerId: string
  playerName: string
  injuryName: string
}

export interface BoardObjectivePayload {
  objectiveLabel: string
  season: number
  divisionName: string
  success?: boolean
  position?: number
}

export interface ManagerFiredPayload {
  clubName: string
  season: number
}

export interface ClubChangedPayload {
  clubName: string
  divisionName: string
}

export interface ConstructionCompletedPayload {
  kind: 'court' | 'building' | 'building_upgrade' | 'court_upgrade'
  label: string
}
